import React, { useState, useRef, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  CircularProgress,
} from "@mui/material";
import { saveContainer, uploadImageToStrapi } from "../strapi/queries";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

/**
 * Modal component for adding a new container
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Function to call when the modal is closed
 * @param {string} props.parentContainerId - ID of the parent container
 * @param {Function} props.onSuccess - Function to call when a container is successfully created
 * @returns {JSX.Element} - The modal component
 */
const AddContainerModal = ({ open, onClose, parentContainerId, onSuccess }) => {
  const [containerName, setContainerName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Image related states
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({
    unit: "%",
    width: 75,
    aspect: 3 / 4,
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [removeBackground, setRemoveBackground] = useState(true);
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const [processedImage, setProcessedImage] = useState(null);

  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);

  const handleClose = () => {
    setContainerName("");
    setError(null);
    setSelectedImage(null);
    setImageSrc(null);
    setCompletedCrop(null);
    setProcessedImage(null);
    onClose();
  };

  // Handle image selection
  const handleImageSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setSelectedImage(file);

      const reader = new FileReader();
      reader.addEventListener("load", () => setImageSrc(reader.result));
      reader.readAsDataURL(file);
    }
  };

  // Handle image load
  const onImageLoad = useCallback((img) => {
    imgRef.current = img;

    // Set initial crop to center of image with 3:4 aspect ratio
    const width = Math.min(75, (img.width / img.height) * 75 * (3 / 4));
    setCrop({
      unit: "%",
      width,
      height: (width / 3) * 4,
      x: (100 - width) / 2,
      y: (100 - (width / 3) * 4) / 2,
      aspect: 3 / 4,
    });
  }, []);

  // Generate cropped image
  const generateCroppedImage = useCallback(() => {
    if (!completedCrop || !imgRef.current || !previewCanvasRef.current) {
      return null;
    }

    const image = imgRef.current;
    const canvas = previewCanvasRef.current;
    const crop = completedCrop;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext("2d");

    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );

    return canvas;
  }, [completedCrop]);

  // Resize image to reduce file size
  const resizeImage = useCallback((canvas, maxWidth = 400, maxHeight = 533, quality = 0.8) => {
    return new Promise((resolve) => {
      // Create a new canvas for the resized image
      const resizeCanvas = document.createElement("canvas");
      const ctx = resizeCanvas.getContext("2d");
      
      // Get original dimensions
      let width = canvas.width;
      let height = canvas.height;
      
      // Calculate new dimensions while maintaining aspect ratio
      if (width > height) {
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = Math.round((width * maxHeight) / height);
          height = maxHeight;
        }
      }
      
      // Set canvas dimensions to the new size
      resizeCanvas.width = width;
      resizeCanvas.height = height;
      
      // Draw the original image at the new size
      ctx.drawImage(canvas, 0, 0, width, height);
      
      // Convert to blob with specified quality
      resizeCanvas.toBlob(
        (blob) => {
          resolve(blob);
        },
        "image/jpeg",
        quality
      );
    });
  }, []);

  // Process image (remove background if needed)
  const processImage = async () => {
    if (!completedCrop) return;

    setIsProcessingImage(true);

    try {
      const canvas = generateCroppedImage();
      if (!canvas) return;

      // Resize the cropped image to reduce file size
      const blob = await resizeImage(canvas);

      if (removeBackground) {
        // Send to background removal service
        const formData = new FormData();
        formData.append("image", blob, "image.png");

        const response = await fetch(
          "https://bg-remover.cr.baeren-lechtal.com/remove",
          {
            method: "POST",
            body: formData,
          }
        );

        if (!response.ok) {
          throw new Error("Background removal failed");
        }

        const processedBlob = await response.blob();
        const processedUrl = URL.createObjectURL(processedBlob);
        setProcessedImage({
          url: processedUrl,
          blob: processedBlob,
        });
      } else {
        // Use the cropped image without background removal
        const url = URL.createObjectURL(blob);
        setProcessedImage({
          url,
          blob,
        });
      }
    } catch (err) {
      console.error("Error processing image:", err);
      setError(
        "Fehler bei der Bildverarbeitung. Bitte versuchen Sie es erneut."
      );
    } finally {
      setIsProcessingImage(false);
    }
  };

  const handleSubmit = async () => {
    if (!containerName.trim()) {
      setError("Bitte geben Sie einen Namen ein.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      let imageId = null;

      // Upload image to Strapi if we have a processed image
      if (processedImage) {
        const imageResult = await uploadImageToStrapi(processedImage.blob);
        if (imageResult && imageResult.id) {
          imageId = imageResult.id;
        }
      }

      const containerData = {
        name: containerName,
        parent_container: parentContainerId,
        ...(imageId && { image: imageId }),
      };

      const result = await saveContainer(containerData);

      if (result && result.error) {
        setError(
          result.error.message || "Fehler beim Speichern des Containers."
        );
      } else {
        handleClose();
        if (onSuccess) onSuccess();
      }
    } catch (err) {
      setError(
        "Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut."
      );
      console.error("Error saving container:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Neuen Container erstellen</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Container Name"
            type="text"
            fullWidth
            value={containerName}
            onChange={(e) => setContainerName(e.target.value)}
            error={!!error}
            helperText={error}
            disabled={isSubmitting}
          />

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Bild hinzufügen (optional)
            </Typography>

            {!imageSrc && (
              <Button
                variant="outlined"
                component="label"
                sx={{ mt: 1, color: "#10527e", borderColor: "#10527e" }}
                size="small"
                disabled={isSubmitting}
              >
                Bild auswählen
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleImageSelect}
                />
              </Button>
            )}

            {imageSrc && !processedImage && (
              <>
                <Box
                  sx={{
                    mt: 2,
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    gutterBottom
                    style={{
                      width: "100%",
                      justifyContent: "center",
                      display: "flex",
                    }}
                  >
                    Bitte wählen Sie den gewünschten Bildausschnitt
                  </Typography>
                  <div
                    style={{
                      width: "100%",
                      justifyContent: "center",
                      display: "flex",
                    }}
                  >
                    <ReactCrop
                      crop={crop}
                      onChange={(c) => setCrop(c)}
                      onComplete={(c) => setCompletedCrop(c)}
                      aspect={3 / 4}
                      circularCrop={false}
                    >
                      <img
                        src={imageSrc}
                        alt="zu beschneidendes Bild"
                        onLoad={(e) => onImageLoad?.(e.currentTarget)} // falls du etwas initial berechnen willst
                        style={{
                          //maxWidth: "100%",
                          maxHeight: "200px",
                          maxWidth: "200px",
                          display: "block",
                        }}
                        crossOrigin="anonymous" // nötig, wenn das Bild von einer anderen Domain kommt
                      />
                    </ReactCrop>

                    <canvas
                      ref={previewCanvasRef}
                      style={{
                        display: "none", // sichtbar machen, wenn du die Vorschau zeigen willst
                        width: completedCrop?.width ?? 0,
                        height: completedCrop?.height ?? 0,
                      }}
                    />
                  </div>
                </Box>

                <FormControlLabel
                  style={{
                    width: "100%",
                    justifyContent: "center",
                    display: "flex",
                  }}
                  control={
                    <Checkbox
                      size="small"
                      checked={removeBackground}
                      onChange={(e) => setRemoveBackground(e.target.checked)}
                      disabled={isSubmitting || isProcessingImage}
                    />
                  }
                  label="Hintergrund entfernen"
                />

                <Box
                  sx={{ mt: 1, display: "flex", gap: 2 }}
                  style={{
                    width: "100%",
                    justifyContent: "center",
                    display: "flex",
                  }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={processImage}
                    sx={{ color: "#10527e", borderColor: "#10527e" }}
                    disabled={
                      !completedCrop || isSubmitting || isProcessingImage
                    }
                  >
                    {isProcessingImage ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Verarbeite...
                      </>
                    ) : (
                      "Bild verarbeiten"
                    )}
                  </Button>

                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={() => {
                      setSelectedImage(null);
                      setImageSrc(null);
                      setCompletedCrop(null);
                    }}
                    disabled={isSubmitting || isProcessingImage}
                  >
                    Abbrechen
                  </Button>
                </Box>
              </>
            )}

            {processedImage && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="body2"
                  gutterBottom
                  style={{
                    width: "100%",
                    justifyContent: "center",
                    display: "flex",
                  }}
                >
                  Verarbeitetes Bild:
                </Typography>
                <div
                  style={{
                    width: "100%",
                    justifyContent: "center",
                    display: "flex",
                  }}
                >
                  <Box
                    component="img"
                    src={processedImage.url}
                    sx={{
                      maxWidth: "200px",
                      maxHeight: "200px",
                      objectFit: "contain",
                      mt: 1,
                      border: "1px solid #ddd",
                    }}
                  />
                </div>
                <div
                  style={{
                    width: "100%",
                    justifyContent: "center",
                    display: "flex",
                  }}
                >
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      setProcessedImage(null);
                      setSelectedImage(null);
                      setImageSrc(null);
                      setCompletedCrop(null);
                    }}
                    sx={{ mt: 1 }}
                    size="small"
                    disabled={isSubmitting}
                  >
                    Bild entfernen
                  </Button>
                </div>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          sx={{ color: "#10527e" }}
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Abbrechen
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isSubmitting}
          sx={{
            backgroundColor: "#10527e",
            color: "white",
            "&:hover": {
              backgroundColor: "#282c34",
            },
            "&.Mui-disabled": {
              backgroundColor: "#cccccc",
              color: "#666666",
            },
          }}
        >
          {isSubmitting ? "Speichern..." : "Speichern"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddContainerModal;
